import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faVideo, 
  faClock, 
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import { Button } from '@mantine/core';
import { ProjectWithVideos, SocialPost } from './types';
import PublishStatePill from './PublishStatePill';
import { normalizeS3Url } from '../../utils/urlUtils';
import styles from './SocialMediaLanding.module.css';

interface SocialMediaProjectCardProps {
  project: ProjectWithVideos;
  onShare: (project: ProjectWithVideos, selectedVideoUrl: string | null) => void;
  publishedPosts?: SocialPost[]; // Posts for this project
}

const SocialMediaProjectCard: React.FC<SocialMediaProjectCardProps> = ({
  project,
  onShare,
  publishedPosts = []
}) => {
  const { t } = useTranslation('socialMediaProjectCard');
  
  // Get the most recent post for this project (if any)
  const latestPost = publishedPosts.length > 0 ? publishedPosts[0] : null;
  
  // Build list of available video options (in order from newest to oldest processing)
  const videoOptions: Array<{ value: string; label: string; url: string }> = [];
  
  // Check for soundtrack video in both locations
  const soundtrackUrl = project.soundTrackVideoUrl || project.metadata?.soundtrack?.soundtrackVideoUrl;
  
  if (soundtrackUrl) {
    videoOptions.push({ 
      value: 'soundtrack', 
      label: t('versions.soundtrack'), 
      url: soundtrackUrl 
    });
  }
  if (project.captionedVideoUrl) {
    videoOptions.push({ 
      value: 'captioned', 
      label: t('versions.captioned'), 
      url: project.captionedVideoUrl 
    });
  }
  if (project.generatedVideoUrl) {
    videoOptions.push({ 
      value: 'generated', 
      label: t('versions.generated'), 
      url: project.generatedVideoUrl 
    });
  }
  if (project.originalVideoUrl) {
    videoOptions.push({ 
      value: 'original', 
      label: t('versions.original'), 
      url: project.originalVideoUrl 
    });
  }
  
  // Default to first available option (most recent processing)
  const [selectedVideoOption, setSelectedVideoOption] = useState(
    videoOptions.length > 0 ? videoOptions[0].value : ''
  );
  
  // Get current video URL based on selection
  const getVideoUrl = () => {
    const selected = videoOptions.find(opt => opt.value === selectedVideoOption);
    // Normalize S3 URL (decode %2F, %20, etc.)
    return normalizeS3Url(selected?.url);
  };
  
  const videoUrl = getVideoUrl();
  const showVideoSelector = videoOptions.length > 1;
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const handleShare = () => {
    // Pass the selected video URL to the modal
    onShare(project, videoUrl);
  };

  return (
    <div className={styles.projectCard}>
      <div className={styles.projectThumbnail}>
        {videoUrl && (
          <video 
            className={styles.videoPreview}
            poster={project.thumbnail}
            preload="metadata"
            onMouseEnter={(e) => e.currentTarget.play()}
            onMouseLeave={(e) => {
              e.currentTarget.pause();
              e.currentTarget.currentTime = 0;
            }}
          >
            <source src={videoUrl} type="video/mp4" />
          </video>
        )}
        
        {!videoUrl && (
          <img 
            src={project.thumbnail} 
            alt={project.title}
            className={styles.thumbnail}
          />
        )}
        
        <div className={styles.projectType}>
          <FontAwesomeIcon icon={faVideo} />
        </div>
        
        <div className={styles.projectDuration}>
          <FontAwesomeIcon icon={faClock} />
          <span>{project.duration}</span>
        </div>
        
        {/* Show publish state pill if this project has been published */}
        {latestPost && (
          <div className={styles.publishStateBadge}>
            <PublishStatePill 
              state={latestPost.publishState}
              scheduledAt={latestPost.scheduledAt}
              publishedAt={latestPost.publishedAt}
            />
          </div>
        )}
      </div>
      
      <div className={styles.projectInfo}>
        <h3 className={styles.projectTitle}>{project.title}</h3>
        <div className={styles.projectMeta}>
          <span className={styles.projectDate}>
            <FontAwesomeIcon icon={faCalendarAlt} />
            {formatDate(project.date)}
          </span>
        </div>
        
        {/* Video version selector dropdown */}
        {showVideoSelector && (
          <div className={styles.videoSelector}>
            <label className={styles.videoSelectorLabel}>
              {t('videoVersion')}
            </label>
            <select
              className={styles.videoSelectorDropdown}
              value={selectedVideoOption}
              onChange={(e) => setSelectedVideoOption(e.target.value)}
            >
              {videoOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      
      <div className={styles.projectActions}>
        <Button 
          className={styles.shareButton}
          onClick={handleShare}
          disabled={!videoUrl}
        >
          {t('share')}
        </Button>
      </div>
    </div>
  );
};

export default SocialMediaProjectCard;
